import {
  createSettingMetadataFromGroupDefaults,
  type SettingMetadataDefaultsContext,
} from "../../settings-catalog/metadata";
import type {
  SettingAgentEditability,
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  CustomerWidgetFlatTeamSettingGroupById,
  type CustomerWidgetFlatTeamSettingGroupId,
} from "./groups";

interface CustomerWidgetSettingInput extends SettingRegistryMetadataInput {}

interface CustomerWidgetSettingRegistryEntry extends CustomerWidgetSettingInput {
  group: CustomerWidgetFlatTeamSettingGroupId;
}

function setting(
  metadata: CustomerWidgetSettingInput,
): CustomerWidgetSettingInput {
  return metadata;
}

function customerWidgetGroup<
  const Group extends CustomerWidgetFlatTeamSettingGroupId,
  const T extends Record<string, CustomerWidgetSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const CustomerWidgetFlatSettingCatalogRegistry = {
  ...customerWidgetGroup("customer_widget.setup", {
    "customerWidget/general/enableInAppEmbed": setting({
      label: "Enable customer widget on site",
      description: "Shows the customer widget on the storefront.",
      synonyms: [
        "enable customer widget",
        "show customer widget",
        "turn on customer widget",
        "install customer widget",
        "customer widget embed",
      ],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Disabled",
        trueLabel: "Enabled",
      },
    }),
    "customerWidget/general/hideButton": setting({
      label: "Hide widget button",
      description: "Hides the customer widget launcher button.",
      synonyms: [
        "hide chat button",
        "hide widget launcher",
        "hide customer widget button",
        "hide support widget button",
      ],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Show button",
        trueLabel: "Hide button",
      },
    }),
  }),

  ...customerWidgetGroup("customer_widget.appearance", {
    "customerWidget/general/widgetColor": setting({
      label: "Widget color",
      description:
        "Primary brand color used by the storefront customer widget launcher, header, and prominent actions.",
      synonyms: [
        "chat widget color",
        "chat widget colors",
        "colors chat widget",
        "customer widget color",
        "storefront widget color",
        "launcher color",
        "primary widget color",
        "make widget red",
        "red widget",
        "red",
      ],
      valueSemantics: { defaultValue: "#181818", inputKind: "color" },
    }),
    "customerWidget/general/widgetSecondaryColorEnabled": setting({
      label: "Secondary color enabled",
      description: "Allows the customer widget to use the secondary color.",
      synonyms: [
        "secondary widget color",
        "accent widget color",
        "enable secondary color",
        "enable accent color",
        "color options",
      ],
      dependencies: [
        {
          key: "customerWidget/general/widgetSecondaryColor",
          relationship: "controls",
          description: "Controls whether the secondary widget color is used.",
        },
      ],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Off",
        trueLabel: "On",
      },
    }),
    "customerWidget/general/widgetSecondaryColor": setting({
      label: "Widget secondary color",
      description:
        "Secondary brand color used by the storefront customer widget when secondary color is enabled.",
      synonyms: [
        "secondary widget color",
        "accent widget color",
        "chat widget accent color",
      ],
      dependencies: [
        {
          key: "customerWidget/general/widgetSecondaryColorEnabled",
          relationship: "requires",
          description: "Requires secondary color to be enabled.",
        },
      ],
      valueSemantics: { defaultValue: "#666666", inputKind: "color" },
    }),
    "customerWidget/general/widgetPosition": setting({
      label: "Widget position",
      description: "Launcher position on the storefront.",
      synonyms: ["launcher position", "widget placement", "chat placement"],
      valueSemantics: {
        defaultValue: "right",
        options: [
          { value: "left", label: "Left" },
          { value: "right", label: "Right" },
        ],
      },
    }),
    "customerWidget/general/xAdjustment": setting({
      label: "Desktop horizontal adjustment",
      description:
        "Moves the customer widget launcher left or right on desktop.",
      synonyms: [
        "desktop left right adjustment",
        "horizontal widget offset",
        "move widget left",
        "move widget right",
      ],
      valueSemantics: { defaultValue: 0, unit: "px" },
    }),
    "customerWidget/general/yAdjustment": setting({
      label: "Desktop vertical adjustment",
      description: "Moves the customer widget launcher up or down on desktop.",
      synonyms: [
        "desktop up down adjustment",
        "vertical widget offset",
        "move widget up",
        "move widget down",
      ],
      valueSemantics: { defaultValue: 0, unit: "px" },
    }),
    "customerWidget/general/mobileXAdjustment": setting({
      label: "Mobile horizontal adjustment",
      description:
        "Moves the customer widget launcher left or right on mobile.",
      synonyms: [
        "mobile left right adjustment",
        "mobile horizontal widget offset",
      ],
      valueSemantics: { defaultValue: 0, unit: "px" },
    }),
    "customerWidget/general/mobileYAdjustment": setting({
      label: "Mobile vertical adjustment",
      description: "Moves the customer widget launcher up or down on mobile.",
      synonyms: ["mobile up down adjustment", "mobile vertical widget offset"],
      valueSemantics: { defaultValue: 0, unit: "px" },
    }),
    "customerWidget/general/zIndex": setting({
      label: "Widget z-index",
      description: "Stacking order for the customer widget on desktop.",
      synonyms: ["widget stacking order", "widget layer", "z index"],
      valueSemantics: { defaultValue: 1_000_000 },
    }),
    "customerWidget/general/mobileZIndex": setting({
      label: "Mobile widget z-index",
      description:
        "Stacking order for the customer widget on mobile. Leave unset to use the desktop value.",
      synonyms: ["mobile widget stacking order", "mobile widget layer"],
      valueSemantics: { unsetLabel: "Use desktop z-index" },
    }),
    "customerWidget/general/headingLogoUrl": setting({
      label: "Heading logo URL",
      description: "Logo image shown in the customer widget header.",
      synonyms: ["widget logo", "chat widget icon", "header logo"],
      valueSemantics: { inputKind: "url" },
    }),
    "customerWidget/general/mainHeadingText": setting({
      label: "Main heading text",
      description: "Primary heading shown on the customer widget home screen.",
      synonyms: ["welcome text", "greeting text", "widget greeting"],
      valueSemantics: { defaultValue: "Hey!" },
    }),
    "customerWidget/general/homeHeadingText": setting({
      label: "Home heading text",
      description:
        "Secondary heading shown on the customer widget home screen.",
      synonyms: ["home greeting", "welcome heading"],
      valueSemantics: { defaultValue: "What can we do for you today?" },
    }),
    "concierge/general/welcomeMessage": setting({
      label: "Welcome message",
      description:
        "Message the AI sales agent sends when it first greets customers in the customer widget.",
      availability: {
        conditions: [{ type: "evaluator", evaluator: "team.canUseAi" }],
        unavailableBehavior: "hidden",
        reason:
          "AI sales agent widget settings are only visible when AI is available for the team.",
      },
      synonyms: [
        "AI welcome message",
        "AI sales agent greeting",
        "chat welcome message",
        "first greeting",
        "initial greeting",
        "welcome message",
        "widget welcome message",
      ],
      valueSemantics: {
        defaultValue: "Hey there! How can I help you today?",
        inputKind: "textarea",
      },
    }),
    "customerWidget/general/bubbleButtonText": setting({
      label: "Bubble button text",
      description: "Text shown on the collapsed widget launcher bubble.",
      synonyms: ["launcher text", "chat bubble text"],
    }),
    "customerWidget/general/chatWithUsButtonText": setting({
      label: "Chat button text",
      description: "Text shown on the chat call-to-action button.",
      synonyms: ["chat with us text", "chat button label"],
      valueSemantics: { defaultValue: "Ask anything..." },
    }),
    "customerWidget/general/widgetButtonFontSize": setting({
      label: "Button font size",
      description: "Font size for customer widget buttons.",
      valueSemantics: { defaultValue: 14, unit: "px" },
    }),
    "customerWidget/general/widgetHeaderFontSize": setting({
      label: "Header font size",
      description: "Font size for customer widget header text.",
      valueSemantics: { defaultValue: 30, unit: "px" },
    }),
    "customerWidget/general/widgetMessageBubbleFontSize": setting({
      label: "Message bubble font size",
      description: "Font size for message bubbles inside the customer widget.",
      valueSemantics: { defaultValue: 12, unit: "px" },
    }),
    "customerWidget/general/widgetFontFamily": setting({
      label: "Widget font family",
      description: "Font family used by the customer widget.",
    }),
    "customerWidget/general/openToWelcomePage": setting({
      label: "Open to welcome page",
      description: "Opens the customer widget to the welcome page by default.",
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Open to default page",
        trueLabel: "Open to welcome page",
      },
    }),
    "customerWidget/general/mobileFullscreen": setting({
      label: "Mobile fullscreen",
      description: "Shows the customer widget fullscreen on mobile devices.",
      valueSemantics: {
        defaultValue: true,
        falseLabel: "Embedded on mobile",
        trueLabel: "Fullscreen on mobile",
      },
    }),
    "customerWidget/general/hideWidgetOnProductPages": setting({
      label: "Hide widget on product pages",
      description: "Hides the customer widget launcher on product pages.",
      synonyms: [
        "hide widget on product pages",
        "hide chat on product pages",
        "product page widget visibility",
      ],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Show on product pages",
        trueLabel: "Hide on product pages",
      },
    }),
    "customerWidget/general/gradientSetting/gradientStrength": setting({
      label: "Gradient strength",
      description:
        "Intensity of the customer widget gradient effect, from none to maximum.",
      synonyms: ["widget gradient", "gradient intensity"],
      valueSemantics: { defaultValue: 0.3 },
    }),
    "customerWidget/general/gradientSetting/inverted": setting({
      label: "Invert gradient",
      description: "Inverts the customer widget gradient direction.",
      synonyms: ["inverted gradient", "gradient direction"],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Normal gradient",
        trueLabel: "Inverted gradient",
      },
    }),
    "customerWidget/general/customCss": setting({
      label: "Custom CSS",
      description: "Custom CSS applied to the customer widget.",
      synonyms: ["widget CSS", "custom widget CSS", "customer widget styles"],
      risk: {
        level: "high",
        reason:
          "Custom CSS can break storefront widget rendering or conflict with merchant storefront styles.",
      },
      valueSemantics: { defaultValue: "", inputKind: "textarea" },
    }),
  }),

  ...customerWidgetGroup("customer_widget.self_serve", {
    "customerWidget/general/selfServe/startReturnEnabled": setting({
      label: "Start return enabled",
      description:
        "Shows the start return self-serve action in the customer widget.",
      synonyms: ["start return button", "returns action", "return self serve"],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Hidden",
        trueLabel: "Shown",
      },
    }),
    "customerWidget/general/selfServe/startReturnLabel": setting({
      label: "Start return label",
      description:
        "Customer-facing label for the start return self-serve action.",
      synonyms: ["return button label", "start exchange label"],
      dependencies: [
        {
          key: "customerWidget/general/selfServe/startReturnEnabled",
          relationship: "requires",
          description: "Only shown when the start return action is enabled.",
        },
      ],
      valueSemantics: { defaultValue: "Start an exchange or return" },
    }),
    "customerWidget/general/selfServe/startClaimEnabled": setting({
      label: "Start claim enabled",
      description:
        "Shows the start claim self-serve action in the customer widget.",
      synonyms: ["start claim button", "claim self serve", "warranty claim"],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Hidden",
        trueLabel: "Shown",
      },
    }),
    "customerWidget/general/selfServe/editOrderLabel": setting({
      label: "Edit order label",
      description:
        "Customer-facing label for the edit order self-serve action.",
      synonyms: ["edit order button label", "order editing label"],
      valueSemantics: { defaultValue: "Edit order" },
    }),
    "customerWidget/general/selfServe/trackPackageEnabled": setting({
      label: "Track package enabled",
      description:
        "Shows the track package self-serve action in the customer widget.",
      synonyms: ["track package button", "tracking action"],
      valueSemantics: {
        defaultValue: true,
        falseLabel: "Hidden",
        trueLabel: "Shown",
      },
    }),
    "customerWidget/general/selfServe/trackPackageSettingLabel": setting({
      label: "Track package label",
      description:
        "Customer-facing label for the track package self-serve action.",
      synonyms: ["tracking button label", "package tracking label"],
      dependencies: [
        {
          key: "customerWidget/general/selfServe/trackPackageEnabled",
          relationship: "requires",
          description: "Only shown when the track package action is enabled.",
        },
      ],
      valueSemantics: { defaultValue: "Track a package" },
    }),
  }),

  ...customerWidgetGroup("customer_widget.ai_product_form", {
    "concierge/questionPromptCount": setting({
      label: "Suggested question count",
      description:
        "Number of suggested product questions shown in the AI product form.",
      synonyms: [
        "number of suggested questions",
        "question prompt count",
        "AI suggested questions",
      ],
      valueSemantics: { defaultValue: 3 },
    }),
    "concierge/placeProductFormFromThemeExtension": setting({
      label: "Automatic product form placement",
      description:
        "Automatically places the AI product form on product pages through the theme extension.",
      synonyms: [
        "enable product form placement",
        "automatic AI product form placement",
        "place product question form",
      ],
      dependencies: [
        {
          key: "concierge/themeExtensionProductFormPlaceholder",
          relationship: "controls",
          description:
            "Controls whether product form customization fields are shown.",
        },
        {
          key: "concierge/themeExtensionProductFormButtonText",
          relationship: "controls",
          description:
            "Controls whether product form customization fields are shown.",
        },
      ],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Manual placement",
        trueLabel: "Automatic placement",
      },
    }),
    "concierge/themeExtensionProductFormPlaceholder": setting({
      label: "Product form placeholder",
      description: "Placeholder text shown in the AI product form.",
      synonyms: ["product form placeholder", "AI form placeholder"],
      dependencies: [
        {
          key: "concierge/placeProductFormFromThemeExtension",
          relationship: "requires",
          description:
            "Shown when automatic AI product form placement is enabled.",
        },
      ],
      valueSemantics: { defaultValue: "Ask anything about this product" },
    }),
    "concierge/themeExtensionProductFormButtonText": setting({
      label: "Product form button text",
      description: "Button text shown in the AI product form.",
      synonyms: ["AI form button text", "product question form button"],
      dependencies: [
        {
          key: "concierge/placeProductFormFromThemeExtension",
          relationship: "requires",
          description:
            "Shown when automatic AI product form placement is enabled.",
        },
      ],
      valueSemantics: { defaultValue: "Ask AI" },
    }),
    "concierge/themeExtensionProductFormFontSize": setting({
      label: "Product form font size",
      description:
        "Font size used by the AI product form, for example 14px, 1rem, or inherit.",
      dependencies: [
        {
          key: "concierge/placeProductFormFromThemeExtension",
          relationship: "requires",
          description:
            "Shown when automatic AI product form placement is enabled.",
        },
      ],
      valueSemantics: { defaultValue: "inherit" },
    }),
    "concierge/themeExtensionProductFormBorderRadius": setting({
      label: "Product form border radius",
      description:
        "Border radius used by AI product form elements, for example 4px or 0.5rem.",
      dependencies: [
        {
          key: "concierge/placeProductFormFromThemeExtension",
          relationship: "requires",
          description:
            "Shown when automatic AI product form placement is enabled.",
        },
      ],
      valueSemantics: { defaultValue: "0px" },
    }),
    "concierge/themeExtensionProductFormButtonBackgroundColor": setting({
      label: "Product form button background color",
      description: "Background color for the AI product form button.",
      synonyms: ["AI product form button color"],
      dependencies: [
        {
          key: "concierge/placeProductFormFromThemeExtension",
          relationship: "requires",
          description:
            "Shown when automatic AI product form placement is enabled.",
        },
      ],
      valueSemantics: { defaultValue: "#000", inputKind: "color" },
    }),
    "concierge/themeExtensionProductFormButtonTextColor": setting({
      label: "Product form button text color",
      description: "Text color for the AI product form button.",
      dependencies: [
        {
          key: "concierge/placeProductFormFromThemeExtension",
          relationship: "requires",
          description:
            "Shown when automatic AI product form placement is enabled.",
        },
      ],
      valueSemantics: { defaultValue: "#fff", inputKind: "color" },
    }),
    "concierge/themeExtensionProductFormCustomCss": setting({
      label: "Product form custom CSS",
      description: "Custom CSS applied to the AI product form.",
      synonyms: ["AI product form CSS", "product form custom styles"],
      dependencies: [
        {
          key: "concierge/placeProductFormFromThemeExtension",
          relationship: "requires",
          description:
            "Shown when automatic AI product form placement is enabled.",
        },
      ],
      risk: {
        level: "high",
        reason:
          "Custom CSS can break product-page form rendering or conflict with merchant storefront styles.",
      },
      valueSemantics: { defaultValue: "", inputKind: "textarea" },
    }),
  }),

  ...customerWidgetGroup("customer_widget.accounts", {
    "customerWidget/customerAccounts/registerHeader": setting({
      label: "Register header",
      description:
        "Heading text prompting customers to sign in or create an account.",
      synonyms: ["account sign in heading", "register heading"],
      valueSemantics: { defaultValue: "Sign in or create an account" },
    }),
    "customerWidget/customerAccounts/registerSubtext": setting({
      label: "Register subtext",
      description:
        "Supporting copy explaining why customers should sign in or create an account.",
      synonyms: ["account sign in subtext", "register subtext"],
      valueSemantics: {
        defaultValue:
          "Sign in to manage orders, check shipping status, view saved items, and browse product recommendations.",
        inputKind: "textarea",
      },
    }),
    "customerWidget/customerAccounts/showAccountsPage": setting({
      label: "Show accounts button",
      description: "Shows the accounts button on the widget home page.",
      synonyms: ["accounts button", "show account page", "account tab button"],
      valueSemantics: {
        defaultValue: true,
        falseLabel: "Hidden",
        trueLabel: "Shown",
      },
    }),
    "concierge/emailSignUp/incentivize": setting({
      label: "Incentivize email signups",
      description:
        "Offers a discount or reward when customers sign up with their email.",
      owner: {
        kind: "teamSettings",
        path: "settings.concierge.emailSignUp",
        product: "customer-widget",
      },
      synonyms: ["email signup incentive", "signup discount"],
      dependencies: [
        {
          key: "concierge/emailSignUp/signUpDiscountAmount",
          relationship: "controls",
          description: "Controls whether signup reward fields are shown.",
        },
      ],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "No incentive",
        trueLabel: "Offer incentive",
      },
    }),
    "concierge/emailSignUp/signUpDiscountAmount": setting({
      label: "Signup discount amount",
      description: "Discount percentage offered for email signups.",
      owner: {
        kind: "teamSettings",
        path: "settings.concierge.emailSignUp",
        product: "customer-widget",
      },
      synonyms: ["signup reward", "email signup discount"],
      dependencies: [
        {
          key: "concierge/emailSignUp/incentivize",
          relationship: "requires",
          description: "Only applies when email signup incentives are enabled.",
        },
      ],
      valueSemantics: { defaultValue: 15, unit: "%" },
    }),
    "concierge/emailSignUp/buttonLabel": setting({
      label: "Signup incentive button label",
      description: "Button label shown for the email signup incentive.",
      owner: {
        kind: "teamSettings",
        path: "settings.concierge.emailSignUp",
        product: "customer-widget",
      },
      dependencies: [
        {
          key: "concierge/emailSignUp/incentivize",
          relationship: "requires",
          description: "Only shown when email signup incentives are enabled.",
        },
      ],
      valueSemantics: { defaultValue: "Earn a 15% discount when you sign in" },
    }),
    "customerWidget/customerAccounts/accountTabSections": setting({
      label: "Account tab modules",
      description:
        "Sections shown in the customer account tab, including their order, enabled state, and headers.",
      synonyms: ["account modules", "account tab sections"],
      changeGuidance: {
        summary:
          "Open the settings page for detailed account module ordering and per-module header edits.",
      },
    }),
  }),
};

export type CustomerWidgetFlatTeamSettingKey =
  keyof typeof CustomerWidgetFlatSettingCatalogRegistry;

const CustomerWidgetFlatTeamSettingKeys = Object.keys(
  CustomerWidgetFlatSettingCatalogRegistry,
) as CustomerWidgetFlatTeamSettingKey[];

const CustomerWidgetFlatTeamSettingKeySet = new Set<string>(
  CustomerWidgetFlatTeamSettingKeys,
);

export function isCustomerWidgetFlatTeamSettingKey(
  key: string,
): key is CustomerWidgetFlatTeamSettingKey {
  return CustomerWidgetFlatTeamSettingKeySet.has(key);
}

export const CustomerWidgetFlatTeamSettingCatalogDefinitions =
  CustomerWidgetFlatTeamSettingKeys.map((key) => {
    const definition = CustomerWidgetFlatSettingCatalogRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: CustomerWidgetFlatTeamSettingGroupById[definition.group],
        resolveAgentEditability: createCustomerWidgetSettingAgentEditability,
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    CustomerWidgetFlatTeamSettingKey,
    CustomerWidgetFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<CustomerWidgetFlatTeamSettingKey>
  >[];

export const CustomerWidgetFlatTeamSettingMetadataByKey = Object.fromEntries(
  CustomerWidgetFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<CustomerWidgetFlatTeamSettingKey, SettingMetadata>;

function createCustomerWidgetSettingAgentEditability({
  setting,
  group,
  access,
  risk,
  affectsBilling,
}: SettingMetadataDefaultsContext<CustomerWidgetSettingRegistryEntry>): SettingAgentEditability {
  if (setting.agentEditability) {
    return setting.agentEditability;
  }

  if (access.write.mode === "none") {
    return { mode: "never", reason: access.write.reason };
  }

  if (affectsBilling || risk.level === "high" || risk.level === "critical") {
    return {
      mode: "suggestOnly",
      reason: "High-risk settings should be reviewed before saving.",
    };
  }

  return group.agentEditability;
}
